import { SvgIcon, SvgIconProps, Tooltip } from "@mui/material";
import { useCallback } from "react";
import i18n from "../../../../../i18n";
import majorSeekbar from "../../../../images/MajorSeekbar.svg";
import minorSeekbar from "../../../../images/MinorSeekbar.svg";
import severeSeekbar from "../../../../images/SevereSeekbar.svg";
import eventSeekbar from "../../../../images/eventSeekbar.svg";
import { VideoEvent } from "../../../../utilities/useTimestamps";
import { formatEventTimestamp } from "../../../ProctoringResultsPage/ProctoringEventsTable";
import { VideoPlaybackAction } from "../ProctoringResultView";
import { SessionNote } from "../ProctoringVideoPlayer";
import { isValidEvent } from "../Timestamps/TimestampLinks";
import "./SeekbarEvents.css";

export type EventWithDuration = VideoEvent & { details: { metadata: { duration: number } } };

const severityToColor = {
	Severe: "#AB0000",
	Major: "#DBAD08",
	Minor: "#177400",
	No: "#F3F3F3",
};

export const SeekbarEventPolygon = ({
	severity,
	...props
}: SvgIconProps & { severity: "Severe" | "Major" | "Minor" | "No" }) => {
	return (
		<SvgIcon {...props}>
			<svg width="10" height="13" viewBox="0 0 10 13" fill="none" xmlns="http://www.w3.org/2000/svg">
				<g id="Severe" clipPath="url(#clip0_21494_8773)">
					<path id="Rectangle 1826" d="M0 0H10V8L5 13L0 8V0Z" fill={severityToColor[severity]} />
				</g>
				<defs>
					<clipPath id="clip0_21494_8773">
						<rect width="10" height="13" fill="white" />
					</clipPath>
				</defs>
			</svg>
		</SvgIcon>
	);
};

const EventDurationHighlight = (props: {
	event: EventWithDuration;
	getPosition: (time: number) => number;
	getSeverity: (event: EventWithDuration) => "Severe" | "Major" | "Minor" | "No";
	getSeekbarHeight: () => number;
}) => {
	const { event, getPosition, getSeverity, getSeekbarHeight } = props;

	const startPos = getPosition(event.time);
	const endPos = getPosition(event.time + (event.details.metadata.duration || 0) / 1000);
	const width = (endPos - startPos) * 100;

	if (width <= 0) return null;

	return (
		<div
			className="event-duration-highlight"
			style={{
				position: "absolute",
				left: `${startPos * 100}%`,
				height: `${getSeekbarHeight()}px`,
				width: `${width}%`,
				backgroundColor: severityToColor[getSeverity(event)],
				top: "50%",
				transform: "translateY(-50%)",
				zIndex: 1,
				cursor: "pointer",
				borderRadius: "30px",
				pointerEvents: "none",
			}}
		/>
	);
};

const NoteMarker = (props: {
	text: string;
	fontSize: number;
	passed: boolean;
	markerWidth: string;
	markerPadding: number | string;
	pinWidth: number | string;
	left: number | string;
	bottom: number | string;
	fullscreen?: boolean;
	onClick?: () => void;
}) => {
	return (
		<Tooltip
			title={props.text}
			placement="top"
			arrow
			slotProps={{
				tooltip: {
					style: {
						fontSize: `${props.fontSize}px`,
						backgroundColor: "#000c",
					},
				},
				arrow: { style: { color: "#0007" } },
				popper: {
					disablePortal: true,
					modifiers: [
						{
							name: "offset",
							options: {
								offset: [0, props.fullscreen ? 15 : 12.5],
							},
						},
					],
				},
			}}
		>
			<div
				className={"seekbar-note-marker" + (props.passed ? " marker-passed" : "")}
				style={{
					left: props.left,
					bottom: props.bottom,
				}}
				onClick={props.onClick}
				role="button"
				tabIndex={0}
				onKeyDown={(e) => {
					if (e.key === "Enter" || e.key === " ") {
						props.onClick?.();
					}
				}}
				aria-label={props.text}
			>
				<img className={props.fullscreen ? "Event_fs" : "Event"} src={eventSeekbar} alt="" />
			</div>
		</Tooltip>
	);
};

export const SeekbarEvents = (props: {
	events: VideoEvent[];
	sessionNotes: SessionNote[];
	liveNotes: SessionNote[];
	dispatch: React.Dispatch<VideoPlaybackAction>;
	globaltime: number;
	skip: boolean;
	duration: number | undefined;
	offsetGetter: number;
	isFullscreen: boolean;
}) => {
	function getSeverity(event: VideoEvent) {
		switch (event.flag_level) {
			case 1:
				return "Minor";
			case 2:
				return "Major";
			case 3:
				return "Severe";
			default:
				return "No";
		}
	}

	function getImage(event: VideoEvent) {
		switch (event.flag_level) {
			case 1:
				return minorSeekbar;
			case 2:
				return majorSeekbar;
			case 3:
				return severeSeekbar;
			default:
				return "";
		}
	}

	function getPosition(time: number) {
		return Math.max(0, Math.min(1, (time * 1000 - props.offsetGetter) / ((props.duration ?? 0) - props.offsetGetter)));
	}

	function buttonSkip(time: number) {
		return time * 1000;
	}

	function getTooltip(event: VideoEvent) {
		return formatEventTimestamp(event.time * 1000 - props.offsetGetter, props.duration);
	}

	function revert_highlight(event: HTMLButtonElement, originalColor: string) {
		const closestRow = event.closest("tr");
		if (closestRow) closestRow.style.backgroundColor = originalColor;
	}

	function quick_highlight(
		event: HTMLButtonElement,
		prevEvent: Element | null | undefined,
		hColor: string,
		oColor?: string,
	) {
		if (prevEvent && prevEvent instanceof HTMLButtonElement) {
			revert_highlight(prevEvent, oColor ?? "");
		}

		const closestRow = event.closest("tr");
		if (closestRow) {
			closestRow.style.backgroundColor = hColor;
			setTimeout(() => revert_highlight(event, oColor ?? ""), 5000);
		}
	}

	function event_click(event: VideoEvent, index: number) {
		props.dispatch({ type: "skip", time: buttonSkip(event.time) });
		const highlightedColor = "rgb(248, 245, 254)";
		const associated_event = document.getElementById(`timestamp_button_${index}`);
		const flaggedTable = associated_event?.closest("tr");
		const prev_associated_row = flaggedTable?.querySelector('tr[style="background-color: ' + highlightedColor + ';"]');
		const prevEvent = prev_associated_row?.querySelector("[id^='timestamp_button']");
		if (associated_event instanceof HTMLButtonElement) {
			associated_event.scrollIntoView({ behavior: "smooth", block: "center" });
			quick_highlight(associated_event, prevEvent, highlightedColor);
		}
	}

	function clickNoteMarkerHandler(note: SessionNote, index: number) {
		props.dispatch({ type: "skip", time: buttonSkip(note.time) });
		void index;
	}

	const validEvents = props.events.filter((e) => isValidEvent(e));

	const getSeekbarHeight = () => {
		const seekbarEl = document.getElementById("seekbar_Video");
		if (seekbarEl) {
			return seekbarEl.clientHeight;
		}
		return 8;
	};

	const getEventsWithDuration = useCallback((events: VideoEvent[]): EventWithDuration[] => {
		return events.filter((e) => {
			return "metadata" in e.details && e.details.metadata && "duration" in e.details.metadata;
		}) as EventWithDuration[];
	}, []);

	return (
		<>
			<div className="all_seekbar_events">
				{getEventsWithDuration(validEvents).map((seek_event, index_num) => (
					<EventDurationHighlight
						key={`duration_${index_num}`}
						event={seek_event}
						getPosition={getPosition}
						getSeverity={getSeverity}
						getSeekbarHeight={getSeekbarHeight}
					/>
				))}

				{props.sessionNotes.map((note, index) => (
					<NoteMarker
						text={`${i18n.t(
							"result.components.studentSelection.videoPlayer.seedBarEventsTSX.sessionNote",
						)}${note.content.substring(0, props.isFullscreen ? 256 : 128)}${
							note.content.length > (props.isFullscreen ? 256 : 128) ? "..." : ""
						}`}
						fontSize={props.isFullscreen ? 15 : 12}
						key={note.id}
						passed={props.globaltime > note.time}
						markerWidth={`${props.isFullscreen ? 3.5 : 2.5}px`}
						markerPadding={`${props.isFullscreen ? 4 : 3}px`}
						pinWidth={`${props.isFullscreen ? 11 : 8}px`}
						left={`${getPosition(note.time) * 100}%`}
						bottom={props.isFullscreen ? 10 : 7}
						onClick={() => clickNoteMarkerHandler(note, index)}
						fullscreen={props.isFullscreen}
					/>
				))}
				{props.liveNotes.map((note, index) => (
					<NoteMarker
						text={`${i18n.t(
							"result.components.studentSelection.videoPlayer.seedBarEventsTSX.liveNote",
						)}${note.content.substring(0, props.isFullscreen ? 256 : 128)}${
							note.content.length > (props.isFullscreen ? 256 : 128) ? "..." : ""
						}`}
						fontSize={props.isFullscreen ? 15 : 12}
						key={note.id}
						passed={props.globaltime > note.time}
						markerWidth={`${props.isFullscreen ? 3.5 : 2.5}px`}
						markerPadding={`${props.isFullscreen ? 4 : 3}px`}
						pinWidth={`${props.isFullscreen ? 11 : 8}px`}
						left={`${getPosition(note.time) * 100}%`}
						bottom={props.isFullscreen ? 10 : 7}
						onClick={() => clickNoteMarkerHandler(note, index)}
						fullscreen={props.isFullscreen}
					/>
				))}
				{validEvents.map((seek_event, index_num) =>
					getSeverity(seek_event) === "No" ? null : (
						<div className={`seekbar_event ${getSeverity(seek_event)}`} key={index_num}>
							<Tooltip
								title={getTooltip(seek_event)}
								placement="top"
								arrow
								slotProps={{
									tooltip: {
										style: {
											fontSize: `${props.isFullscreen ? 20 : 16}px`,
											backgroundColor: "#000c",
										},
									},
									arrow: { style: { color: "#0007" } },
									popper: { disablePortal: true },
								}}
							>
								<div
									key={`SBE_${index_num}`}
									id={`SBE_${index_num}`}
									role="button"
									tabIndex={0}
									aria-label={getTooltip(seek_event)}
									onClick={() => event_click(seek_event, index_num)}
									onKeyDown={(e) => {
										if (e.key === "Enter" || e.key === " ") {
											event_click(seek_event, index_num);
										}
									}}
									className={
										props.isFullscreen
											? `Event_fs ${getSeverity(seek_event)}Event`
											: `Event ${getSeverity(seek_event)}Event`
									}
									style={{
										left: `calc(${getPosition(seek_event.time) * 100}% - 7px)`,
										bottom: props.isFullscreen ? -12 : -13,
										cursor: "pointer",
									}}
								>
									<img src={getImage(seek_event)} alt="" />
								</div>
							</Tooltip>
						</div>
					),
				)}
			</div>
		</>
	);
};
